import { UserContext, MediaUserSnowplowTracker, PageEntityContext, ApplicationEntityContext } from 'hudl-user-tracking';
import { trackCustomEvent } from 'hudl-user-tracking/lib/Snowplow/utils/trackEvent';
import { Logger } from 'hudl-base';
import {
  CONSENT_CATEGORIES,
  hasConsentFor,
  subscribeToConsentChanges,
  isConsentDetermined,
} from './consent-utils';

const BASE_TRACKER_SETTINGS = {
  appId: 'Hudl',
  encodeBase64: true,
  forceSecureTracker: true,
  platform: 'web',

  post: true,
  stateStorageStrategy: 'localStorage',
  userFingerprint: true,
  contexts: {
    webPage: true,
    geolocation: false,
  },
};

export const SNOWPLOW_MIN_VISIT_LENGTH = 120; // seconds
export const SNOWPLOW_HEARTBEAT_INTERVAL = 120; // seconds

const CONTENT_FAVORITE_EVENT_SCHEMA = 'iglu:com.hudl/content_favorite_event/jsonschema/2-0-0';
const USER_CONTENT_ENTITY_SCHEMA = 'iglu:com.hudl/content_user_entity/jsonschema/1-0-0';

export class MediaSnowplowManager {
  static _tracker;
  static _context;
  static _logger = new Logger('MediaSnowplowManager');
  static _unsubscribeConsent = null;
  static _hasPerformanceConsent = false;

  static initialize = mediaSnowplowTrackerContext => {
    if (!mediaSnowplowTrackerContext.isTrackerEnabled || this._tracker) {
      return;
    }
    this._context = mediaSnowplowTrackerContext;

    // Check initial consent state
    if (isConsentDetermined()) {
      this._hasPerformanceConsent = hasConsentFor(CONSENT_CATEGORIES.PERFORMANCE);
    }

    this._tracker = this.createTracker(this._context);

    // If no consent initially, enable anonymous tracking right after creation
    if (!this._hasPerformanceConsent) {
      this._tracker.enableAnonymousTracking({ withSessionTracking: true });
    }

    // Subscribe to consent changes
    this._unsubscribeConsent = subscribeToConsentChanges(activeGroups => {
      const newHasPerformance = activeGroups.includes(CONSENT_CATEGORIES.PERFORMANCE);

      if (newHasPerformance !== this._hasPerformanceConsent) {
        this._hasPerformanceConsent = newHasPerformance;
        this.updateTrackingMode(newHasPerformance);
      }
    });
  };

  /**
   * Updates tracking mode based on consent using Snowplow's anonymous tracking API.
   * When consent is denied, enables anonymous tracking which prevents user identifiers
   * from being sent with events. When consent is granted, disables anonymous tracking
   * and restores full user identification.
   *
   * Requires hudl-user-tracking >= 5.23.0 with Snowplow v2.18.1+
   *
   * @param {boolean} hasPerformanceConsent - Whether performance consent is granted
   */
  static updateTrackingMode = hasPerformanceConsent => {
    if (!this._tracker) {
      return;
    }

    if (hasPerformanceConsent) {
      // Disable anonymous tracking and restore full tracking with userId
      this._tracker.disableAnonymousTracking({ stateStorageStrategy: 'localStorage' });
      // Also restore the userId explicitly
      const globalName = this._tracker.globalSnowplowName;
      const userId = this._context?.userId;
      if (typeof window !== 'undefined' && window[globalName]) {
        window[globalName]('setUserId', userId ? String(userId) : null);
      }
    } else {
      // Enable anonymous tracking - no user identifiers will be sent
      this._tracker.enableAnonymousTracking({ withSessionTracking: true });
    }
  };

  static getTrackerSettings = (isProduction, snowplowCollectorSubdomain) => {
    const domain = isProduction ? 'hudl.com' : 'thorhudl.com';
    const performanceTiming = !isProduction;
    const gaCookies = !isProduction;

    return {
      ...BASE_TRACKER_SETTINGS,
      collectorEndpoint: `${snowplowCollectorSubdomain}.${domain}`,
      cookieDomain: `.${domain}`,
      contexts: {
        ...BASE_TRACKER_SETTINGS.contexts,
        gaCookies,
        performanceTiming,
      },
    };
  };

  static createTracker = mediaSnowplowTrackerContext => {
    const { userId, isProduction, snowplowCollectorSubdomain } = mediaSnowplowTrackerContext;
    const trackerSettings = this.getTrackerSettings(isProduction, snowplowCollectorSubdomain);

    // Only set userId if we have performance consent
    const effectiveUserId = this._hasPerformanceConsent && userId ? String(userId) : null;

    const config = {
      globalSnowplowName: 'hudlSnowplowMediaUserTracker',
      trackerName: 'MediaUserSnowplowTracker',
      trackerSettings,
      userId: effectiveUserId,
    };

    return new MediaUserSnowplowTracker(config);
  };

  static trackPagePings = () => {
    if (!this._tracker) {
      return;
    }
    this._tracker.trackPagePings(SNOWPLOW_MIN_VISIT_LENGTH, SNOWPLOW_HEARTBEAT_INTERVAL);
  };

  static trackPage = screenTrackingPageName => {
    if (!this._tracker || !this._context) {
      return;
    }

    const { userId, role, backdooredUserId } = this._context;

    const userContext = userId && this._hasPerformanceConsent
      ? [
          new UserContext({
            user_id: String(userId),
            role: role || undefined,
            backdoored_user_id: backdooredUserId ? String(backdooredUserId) : null,
          }),
        ]
      : [];

    this._tracker.trackPage(screenTrackingPageName, userContext);
  };

  static trackContentFavoriteEventForAthlete = (actionType, favoritedLocation, athleteEntity, pageType) => {
    if (!this._tracker || !this._context) {
      return;
    }

    if(!athleteEntity.userId) {
      this._logger.log('trackContentFavoriteEventForAthlete called without a userId on athleteEntity', athleteEntity);
      return;
    }

    const contentFavoriteEvent = {
      schema: CONTENT_FAVORITE_EVENT_SCHEMA,
      data: {
        action_type: actionType,
        favorited_location: favoritedLocation,
        content_name: '',
      },
    };

    const userContentEntity = {
      schema: USER_CONTENT_ENTITY_SCHEMA,
      data: {
        user_id: String(athleteEntity.userId),
        user_name: athleteEntity.userName,
      },
    };

    const { userId, role, backdooredUserId } = this._context;

    const contexts = [
      new ApplicationEntityContext({
        app_name: 'Hudl',
      }),
      new PageEntityContext({
        page_url: window.location.href,
        page_type: pageType,
      }),
      userContentEntity,
    ];

    if (userId && this._hasPerformanceConsent) {
      contexts.push(
        new UserContext({
          user_id: String(userId),
          role: role || undefined,
          backdoored_user_id: backdooredUserId ? String(backdooredUserId) : null,
        }),
      );
    }
    trackCustomEvent(this._tracker.globalSnowplowName, contentFavoriteEvent, contexts);
  };
}
